import { Box, Container, Grid2 as Grid, List, ListItem} from "@mui/material";
import { useContext, useState } from "react";
import { rootContext } from "../contexts/rootContext";
import SpinningCube from "../components/SpinningCube";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SupaLink from "../components/SupaLink";
import SupaLinkDialog from "../components/SupaLinkDialog";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HelpIcon from '@mui/icons-material/Help';

function Blog_Abstraction() {
    const context = useContext(rootContext);

    const [open, setOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState(<></>);

    const defaultFontSize = "18px";

    const handleShouldYouCareClick = () => {
        setOpen(true);
        setDialogTitle("Should you care?");
        setDialogContent(<>
            Knowing what natural sources provide your community water can help you advocate for their protection. 
            You might hear about a proposal for a new pig farm next to some river many miles away.
            That might not sound interesting, until you realize that river is the main source of your town's water! 
            And knowing what steps your city is taking to treat water is equally as important.
            Not all water is equal.
            You defintely don't want to face what{" "}
            <a 
                href="https://en.wikipedia.org/wiki/Flint_water_crisis" 
                target="_blank"
                style={{ 
                    color: context.theme.text.contrast1
                }}
            >
                Flint has
                <OpenInNewIcon
                    sx={{
                        fontSize: "20px",
                        marginBottom: "-5px",
                        marginLeft: "2px"
                    }}
                />
            </a>
            {" "}(or still is?).
        </>)
    }

    const handleServiceLineClick = () => {
        setOpen(true);
        setDialogTitle("Service line");
        setDialogContent(<>
            The service line is what carries water from the street to the house. 
            Somewhere along this line will exist a water meter.
            Ownership of this line depends upon where it falls in relation to property boundaries.
            So you may only have visibility and control over part of this component.
        </>)
    }

    const handleWaterMainClick = () => {
        setOpen(true);
        setDialogTitle("Water main");
        setDialogContent(<>
            The water main is a massive pipe that delivers drinking water to all areas of a city. Service lines are connected to the water main.
            It is a vast underground network that is owned by utility companies (public or private).
        </>)
    }

    const handleWaterStorageClick = () => {
        setOpen(true);
        setDialogTitle("Water storage");
        setDialogContent(<>
            The water main pulls from different storage units owned by utility companies.
            These can be water towers or large above/below/on ground tanks.
            Here's one of the larger 
            {" "}<a 
                href="https://maps.app.goo.gl/fTmHC53FqqaGM9TQ8" 
                target="_blank"
                style={{ 
                    color: context.theme.text.contrast1
                }}
            >
                water towers
                <OpenInNewIcon
                    sx={{
                        fontSize: "20px",
                        marginBottom: "-5px",
                        marginLeft: "2px"
                    }}
                />
            </a>{" "} in my area.
            The towers are visible, but not other storage units.
        </>)
    }

    const handleWaterTreatmentClick = () => {
        setOpen(true);
        setDialogTitle("Water treatment");
        setDialogContent(<>
            Before drinking water makes it to storage, it's treated.
            Water treament plants use various processes to make the incoming water safe for drinking.
            Here's a
            {" "}<a 
                href="https://maps.app.goo.gl/jWzpeGyoWbB3n7bh6" 
                target="_blank"
                style={{ 
                    color: context.theme.text.contrast1
                }}
            >
                treatment plant
                <OpenInNewIcon
                    sx={{
                        fontSize: "20px",
                        marginBottom: "-5px",
                        marginLeft: "2px"
                    }}
                />
            </a>{" "}
            in my area.
            These are tightly secured.
        </>)
    }

    const handleNaturalSourcesClick = () => {
        setOpen(true);
        setDialogTitle("Natural sources");
        setDialogContent(<>
            Your drinking water most likely originates from a lake, river, or even ocean. 
            These natural sources are fed from other smaller natural sources. 
            These all make up a watershed. 
            You may not have much direct control over these natural sources, but you have the power to advocate for their protection.
            I originally thought the 
            {" "}<a 
                href="https://en.wikipedia.org/wiki/Arkansas_River" 
                target="_blank"
                style={{ 
                    color: context.theme.text.contrast1
                }}
            >
                Arkansas River
                <OpenInNewIcon
                    sx={{
                        fontSize: "20px",
                        marginBottom: "-5px",
                        marginLeft: "2px"
                    }}
                />
            </a>{" "}
            provided my area's water, but that 
            {" "}<a 
                href="https://encyclopediaofarkansas.net/entries/central-arkansas-water-13709/" 
                target="_blank"
                style={{ 
                    color: context.theme.text.contrast1
                }}
            >
                isn't the case
                <OpenInNewIcon
                    sx={{
                        fontSize: "20px",
                        marginBottom: "-5px",
                        marginLeft: "2px"
                    }}
                />
            </a>
            .
            {" "}<a 
                href="https://maps.app.goo.gl/72beqbT6sShoZAqT8" 
                target="_blank"
                style={{ 
                    color: context.theme.text.contrast1
                }}
            >
                Lake Maumelle
                <OpenInNewIcon
                    sx={{
                        fontSize: "20px",
                        marginBottom: "-5px",
                        marginLeft: "2px"
                    }}
                />
            </a>{" "}
            is my area's primary natural source of water.
        </>)
    }

    return (<Grid size="grow" sx={{ 
            marginTop: "25px",
            marginLeft: "25px", 
            marginRight: "25px", 
            overflowY: "scroll",
            scrollbarColor: `${context.theme.text.contrast4} ${context.theme.background}`
        }}>
        <Box style={{ textAlign: "center", paddingBottom: "40px" }}>
            <Box style={{ fontSize: "50px" }} >Abstraction</Box>
            <Box style={{ fontSize: "25px", paddingBottom: "10px" }} >How much ignorance is bliss?</Box>
            <Box style={{ fontSize: defaultFontSize }} >April 15, 2025</Box>
        </Box>
        <Container maxWidth="md" sx={{p: 0}}>
            <Box style={{ fontSize: defaultFontSize, paddingBottom: "30px" }} >
                Do you know where the water from your faucet comes from?
                Do you care?
                {" "}<SupaLink text="Should you care?" textSize={defaultFontSize} handleClick={handleShouldYouCareClick}/>{" "}
                Water is pretty important.
                And as far as I know, it magically appears when I turn on a faucet.
                Bills are paid for that to happen, but there's much more than money at play.
                I'll be back after some research.
            </Box>
            <Box style={{ fontSize: defaultFontSize, paddingBottom: "30px" }} >
                Alright I'm back. Working backwards from my faucet, here's what I learned.
            </Box>
            <table style={{ paddingBottom: "30px" }}>
                <tr >
                    <th style={{marginRight: "10px"}}></th>
                    <th style={{paddingRight: "10px"}}>Visible?</th>
                    <th>Ownership?</th>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td><b>1.</b>Faucet</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td><b>2.</b>House plumbing</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td><b>3.</b><SupaLink text="Service line" textSize={defaultFontSize} handleClick={handleServiceLineClick}/></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td><b>4.</b><SupaLink text="Water main" textSize={defaultFontSize} handleClick={handleWaterMainClick}/></td>
                    <td style={{textAlign: "center"}}><CancelIcon sx={{color: context.theme.text.contrast1, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CancelIcon sx={{color: context.theme.text.contrast1, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td><b>5.</b><SupaLink text="Water storage" textSize={defaultFontSize} handleClick={handleWaterStorageClick}/></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CancelIcon sx={{color: context.theme.text.contrast1, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td><b>6.</b><SupaLink text="Water treatment" textSize={defaultFontSize} handleClick={handleWaterTreatmentClick}/></td>
                    <td style={{textAlign: "center"}}><CancelIcon sx={{color: context.theme.text.contrast1, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CancelIcon sx={{color: context.theme.text.contrast1, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td><b>7.</b><SupaLink text="Natural sources" textSize={defaultFontSize} handleClick={handleNaturalSourcesClick}/></td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CancelIcon sx={{color: context.theme.text.contrast1, transition: context.theme.transitions.color}} /></td>
                </tr>
            </table>
            <Box style={{ fontSize: defaultFontSize, paddingBottom: "30px" }} >
                Getting that magic water to your faucet is no simple feat. 
                The system behind this is quite complex.
                It's convenient to pay a monthly bill to have all of this taken care of for you.
                But you do have other options available.
                If you wanted to be completely independent you could dig a well, collect rain water, etc.
                Yet in doing so you are taking on this whole complex system yourself.
                Along with independence, you might even be able to produce better quality drinking water.
                Most stick with the convenient route for good reasons.
            </Box>
            <Box style={{ fontSize: defaultFontSize, paddingBottom: "30px" }} >
                Enough splashing around, let's talk software development.
                Check out this spinning 3D cube below.
            </Box>
            <SpinningCube />
            <Box style={{ fontSize: defaultFontSize, paddingBottom: "30px", paddingTop: "30px" }} >
                Pretty neat! Here's the code that creates this 3D scene...
            </Box>
            <blockquote style={{fontSize: defaultFontSize}}><code>{`<`}<span style={{color: context.theme.text.contrast5}}>{`SpinningCube`}</span>{` />`}</code></blockquote>
            <Box style={{ fontSize: defaultFontSize, paddingBottom: "30px", paddingTop: "30px" }} >
                That's right. 
                Just 1 line. 
                Well, not exactly. 
                There's a lot more going on beneath this abstraction.
                Computers only understand 1s and 0s.
                So, let's trace this 1 line of code back to binary. 
            </Box>
            <table style={{ paddingBottom: "30px" }}>
                <tr >
                    <th></th>
                    <th style={{paddingRight: "10px", paddingLeft: "10px"}}>lines<br/> of code</th>
                    <th style={{paddingRight: "10px"}}>Visible?</th>
                    <th>Ownership?</th>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td>{"<SpinningCube />"}</td>
                    <td style={{textAlign: "center"}}>1</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td>{<SupaLink text="SpinningCube.tsx" textSize={defaultFontSize} handleClick={()=>{}}/>}</td>
                    <td style={{textAlign: "center"}}>56</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                <td>{<SupaLink text="three.js" textSize={defaultFontSize} handleClick={()=>{}}/>}</td>
                {
                    //https://github.com/mrdoob/three.js/blob/dev/build/three.core.js
                    }
                    <td style={{textAlign: "center"}}>57,920</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td>{<SupaLink text="WebGL" textSize={defaultFontSize} handleClick={()=>{}}/>}</td>
                    <td style={{textAlign: "center"}}>???</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td>{<SupaLink text="bundle.js" textSize={defaultFontSize} handleClick={()=>{}}/>}</td>
                    <td style={{textAlign: "center"}}>125,074</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td>{<SupaLink text="V8 Engine" textSize={defaultFontSize} handleClick={()=>{}}/>}</td>
                    <td style={{textAlign: "center"}}>{"> 2,000,000"}</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                </tr>
                <tr style={{lineHeight: "50px"}}>
                    <td>{<SupaLink text="binary" textSize={defaultFontSize} handleClick={()=>{}}/>}</td>
                    <td style={{textAlign: "center"}}>???</td>
                    <td style={{textAlign: "center"}}><CheckCircleIcon sx={{color: context.theme.text.contrast3, transition: context.theme.transitions.color}} /></td>
                    <td style={{textAlign: "center"}}><HelpIcon sx={{color: context.theme.text.contrast4, transition: context.theme.transitions.color}} /></td>
                </tr>
            </table>
            <Box style={{ fontSize: defaultFontSize, paddingBottom: "30px", paddingTop: "30px" }} >
                TBD...
            </Box>
        </Container>
        <SupaLinkDialog 
            title={dialogTitle}
            content={dialogContent}
            open={open}
            setOpen={setOpen}
            titleColor={context.theme.text.contrast2}
        />
    </Grid>);
}

export default Blog_Abstraction;